import React, { ReactNode, useContext, useEffect } from 'react';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { ContainerInfoCodeEnum, ExternalOrdersApiTypeEnum, FieldMode, SnCommonConfigDTO, SnPartyConfigDTO, SnModuleSettingsDTO, SnTagSetting, TagCondition, TagEnum } from '@api/ringil-api.v2.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import RingilButton from '@components/framework/buttons/RingilButton.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Chips } from 'primereact/chips';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';

interface ComponentProps {
  companyId: string;
}

type Side = 'owner' | 'inheritor';
type ModeField = 'visible' | 'editable' | 'required';

const SIDES: Side[] = ['owner', 'inheritor'];
// Compact per-tag table: scope | visible | editable | required | default value | visible when | required when.
const TAG_KEYS = Object.values(TagEnum);
// Only enum-backed tags can be referenced in a condition (their values are pickable from a LoV); free-text tags can't.
const CONDITION_TAGS: TagEnum[] = [TagEnum.STATUS, TagEnum.TRANSPORTATION_TYPE, TagEnum.CARGO, TagEnum.CUSTOMS_REGIME];
// Default-value control kind per tag (the value stays a string[] either way). The rest are free text.
const SINGLE_SELECT_TAGS: TagEnum[] = [TagEnum.STATUS, TagEnum.TRANSPORTATION_TYPE, TagEnum.CARGO, TagEnum.CUSTOMS_REGIME, TagEnum.CONTAINER];
const MULTI_SELECT_TAGS: TagEnum[] = [TagEnum.CARRIER_REQUIREMENTS];
// Scalar mode fields share the same (visible, editable, required) triple as tags.
const SCALAR_FIELDS: (keyof SnPartyConfigDTO)[] = ['timeslots', 'manipulationUnitNote', 'stackable', 'amountOfOrder', 'orderValue', 'supplierAndCustomer', 'requestNotifiedPersonInSn'];
const GRID = 'grid grid-cols-[220px_1fr_1fr] gap-3 items-center';

interface Opt {
  value: string;
  label: string;
}
const emptyMode = (): FieldMode => ({ visible: false, editable: false, required: false });
const emptyTag = (): SnTagSetting => ({ ...emptyMode(), defaultValue: [], visibleWhen: undefined, requiredWhen: undefined });

// BE serializes shippingTags as a map and returns a full config; this just hardens against sparse/missing parts.
const normalizeConfig = (c: SnPartyConfigDTO): SnPartyConfigDTO => {
  const loaded = (c?.shippingTags ?? {}) as Partial<Record<TagEnum, SnTagSetting>>;
  const shippingTags = TAG_KEYS.reduce(
    (acc, tag) => {
      acc[tag] = { ...emptyTag(), ...(loaded[tag] ?? {}) };
      return acc;
    },
    {} as Record<TagEnum, SnTagSetting>,
  );
  const scalars = SCALAR_FIELDS.reduce((acc, f) => ({ ...acc, [f]: { ...emptyMode(), ...((c?.[f] as FieldMode) ?? {}) } }), {});
  return { ...c, ...scalars, shippingTags };
};

const ModuleSettingsSupplierNotification: React.FC<ComponentProps> = ({ companyId }) => {
  const { getSnModuleSettings, upsertSnModuleSettings, lovTransportationTypes, lovShipmentStateStatus, lovCargoType, lovCustomsTypes, lovSpecialRequirementsWithParentData } =
    useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);
  const [settings, setSettings] = React.useState<SnModuleSettingsDTO>();
  // Selectable values per tag, loaded from each tag's source (enum/LoV); used by both the condition pickers and the default-value control.
  const [tagOptions, setTagOptions] = React.useState<Partial<Record<TagEnum, Opt[]>>>({});

  useEffect(() => {
    // BE returns owner+inheritor fully populated (a saved row, else the uniform default matrix) — no FE mirroring.
    getSnModuleSettings(companyId, d =>
      setSettings({ common: d.common ?? {}, owner: normalizeConfig(d.owner), inheritor: normalizeConfig(d.inheritor), folders: d.folders ?? { required: false, restrictVisibility: false } }),
    );
  }, [companyId]);

  useEffect(() => {
    lovTransportationTypes(d => setTagOptions(p => ({ ...p, [TagEnum.TRANSPORTATION_TYPE]: d.map(t => ({ value: t.transportationType, label: t.name })) })));
    lovShipmentStateStatus(d => setTagOptions(p => ({ ...p, [TagEnum.STATUS]: d.map(t => ({ value: t.code, label: t.name })) })));
    lovCargoType(d => setTagOptions(p => ({ ...p, [TagEnum.CARGO]: d.map(t => ({ value: t.code, label: t.name })) })));
    lovCustomsTypes(d => setTagOptions(p => ({ ...p, [TagEnum.CUSTOMS_REGIME]: d.map(t => ({ value: t.code, label: t.name })) })));
    setTagOptions(p => ({ ...p, [TagEnum.CONTAINER]: Object.values(ContainerInfoCodeEnum).map(v => ({ value: v, label: v })) }));
    lovSpecialRequirementsWithParentData(companyId, d => setTagOptions(p => ({ ...p, [TagEnum.CARRIER_REQUIREMENTS]: d.map(t => ({ value: t.code, label: t.name })) })));
  }, [companyId]);

  const setAt = (side: Side, path: string, value: unknown) => {
    setSettings(prev => {
      const next = structuredClone(prev) as any;
      const segments = path.split('.');
      let cursor = next[side];
      for (let i = 0; i < segments.length - 1; i++) cursor = cursor[segments[i]];
      cursor[segments[segments.length - 1]] = value;
      return next;
    });
  };

  // Monotone staircase: checking propagates right→left, unchecking propagates left→right. Keeps every triple in FFF/TFF/TTF/TTT.
  const setMode = (side: Side, basePath: string, field: ModeField, value: boolean) => {
    setSettings(prev => {
      const next = structuredClone(prev) as any;
      const segments = basePath.split('.');
      let target = next[side];
      for (const s of segments) target = target[s];
      target[field] = value;
      if (value) {
        if (field === 'required') target.editable = true;
        if (field === 'required' || field === 'editable') target.visible = true;
      } else {
        if (field === 'visible') target.editable = false;
        if (field === 'visible' || field === 'editable') target.required = false;
      }
      return next;
    });
  };

  const save = () => {
    upsertSnModuleSettings(companyId, settings, () => {
      showToastMessage(tr('SnSettings.saved', 'Saved'), tr('SnSettings.savedDetail', 'Supplier notification settings saved'), 'success');
    });
  };

  if (isNullOrUndefined(settings)) return <></>;

  const conditionTagOptions = CONDITION_TAGS.map(t => ({ label: tr(`SnTag.${t}`, t), value: t }));
  const tagOf = (side: Side, key: TagEnum): SnTagSetting => (settings[side].shippingTags as Record<TagEnum, SnTagSetting>)[key];

  // Plain function helpers (not nested components) so editing one cell doesn't remount the subtree.
  const row = (label: string, render: (side: Side) => ReactNode): ReactNode => (
    <div className={GRID}>
      <div className={'text-sm text-content-secondary'}>{label}</div>
      <div>{render('owner')}</div>
      <div>{render('inheritor')}</div>
    </div>
  );

  const colHeader = (): ReactNode => (
    <div className={GRID}>
      <div />
      <div className={'text-xs font-semibold uppercase text-content-tertiary'}>{tr('SnSettings.owner', 'Owner')}</div>
      <div className={'text-xs font-semibold uppercase text-content-tertiary'}>{tr('SnSettings.inheritor', 'Inheritor')}</div>
    </div>
  );

  // Inline visible/editable/required switches with letter labels — used for the scalar mode fields.
  const inlineTriple = (side: Side, basePath: string, mode: FieldMode): ReactNode => (
    <div className={'flex flex-row gap-3 items-center'}>
      {([['visible', 'V'], ['editable', 'E'], ['required', 'R']] as [ModeField, string][]).map(([f, lbl]) => (
        <label key={f} className={'flex items-center gap-1 text-xs text-content-tertiary'}>
          <InputSwitch checked={mode[f]} onChange={e => setMode(side, basePath, f, e.value)} />
          {lbl}
        </label>
      ))}
    </div>
  );

  const modeRow = (label: string, field: keyof SnPartyConfigDTO): ReactNode => row(label, side => inlineTriple(side, field, settings[side][field] as FieldMode));

  const generalBlock = (): ReactNode => (
    <div className={'flex flex-col gap-2'}>
      {colHeader()}
      <div className={'text-xs text-content-tertiary'}>{tr('SnSettings.modeLegend', 'V = Visible · E = Editable · R = Required')}</div>
      {modeRow(tr('SnSettings.timeslots', 'Timeslots'), 'timeslots')}
      {modeRow(tr('SnSettings.manipulationUnitNote', 'Manipulation unit note'), 'manipulationUnitNote')}
      {modeRow(tr('SnSettings.stackable', 'Stackable'), 'stackable')}
      {modeRow(tr('SnSettings.amountOfOrder', 'Amount of order'), 'amountOfOrder')}
      {modeRow(tr('SnSettings.orderValue', 'Order value'), 'orderValue')}
      {modeRow(tr('SnSettings.supplierAndCustomer', 'Supplier and customer'), 'supplierAndCustomer')}
      {modeRow(tr('SnSettings.requestNotifiedPersonInSn', 'Require notified person'), 'requestNotifiedPersonInSn')}
    </div>
  );

  // External orders API is a company-level setting — one value, not per-side (null = off).
  const ordersBlock = (): ReactNode => (
    <div className={'flex flex-col gap-1'}>
      <span className={'text-xs text-content-tertiary'}>{tr('SnSettings.externalOrdersApiType', 'External orders API')}</span>
      <Dropdown
        value={settings.common?.externalOrdersApiType ?? null}
        options={Object.values(ExternalOrdersApiTypeEnum).map(v => ({
          label: tr(`ExternalOrdersApiTypeEnum.${v}`, v),
          value: v,
        }))}
        optionLabel={'label'}
        optionValue={'value'}
        showClear
        placeholder={tr('SnSettings.selectExternalOrdersApiType', 'Select API type')}
        onChange={e => setSettings(p => ({ ...p, common: { ...p.common, externalOrdersApiType: e.value } as SnCommonConfigDTO }))}
        className={'w-full max-w-[420px]'}
      />
    </div>
  );

  const sideLabel = (side: Side) => (side === 'owner' ? tr('SnSettings.owner', 'Owner') : tr('SnSettings.inheritor', 'Inheritor'));

  // Enum-restricted tag dropdown + LoV-fed values picker; shared by visibleWhen and requiredWhen.
  const conditionCell = (side: Side, key: TagEnum, field: 'visibleWhen' | 'requiredWhen', cond?: TagCondition): ReactNode => (
    <div className={'flex flex-row gap-1 items-center'}>
      <Dropdown
        value={cond?.tag}
        options={conditionTagOptions}
        showClear
        placeholder={tr('SnSettings.always', 'Always')}
        // Switching the referenced tag changes the value enum, so reset values to avoid cross-enum leftovers.
        onChange={e => setAt(side, `shippingTags.${key}.${field}`, e.value ? { tag: e.value, values: [] } : undefined)}
        className={'flex-1 min-w-0'}
      />
      <MultiSelect
        value={cond?.values ?? []}
        options={tagOptions[cond?.tag] ?? []}
        optionLabel={'label'}
        optionValue={'value'}
        disabled={!cond?.tag}
        placeholder={tr('SnSettings.selectValues', 'Select values')}
        onChange={e => setAt(side, `shippingTags.${key}.${field}.values`, e.value ?? [])}
        className={'flex-1 min-w-0'}
      />
    </div>
  );

  // Soft coupling: introducing a default auto-marks the tag required (cascades editable+visible); the admin can still uncheck it.
  const onDefaultChange = (side: Side, key: TagEnum, current: string[], next: string[]) => {
    setAt(side, `shippingTags.${key}.defaultValue`, next);
    if (next.length > 0 && current.length === 0) setMode(side, `shippingTags.${key}`, 'required', true);
  };

  const defaultValueCell = (side: Side, key: TagEnum, t: SnTagSetting): ReactNode => {
    if (SINGLE_SELECT_TAGS.includes(key))
      return (
        <Dropdown
          value={t.defaultValue?.[0] ?? null}
          options={tagOptions[key] ?? []}
          optionLabel={'label'}
          optionValue={'value'}
          showClear
          placeholder={tr('SnSettings.noDefault', 'No default')}
          onChange={e => onDefaultChange(side, key, t.defaultValue ?? [], e.value ? [e.value] : [])}
          className={'w-full'}
        />
      );
    if (MULTI_SELECT_TAGS.includes(key))
      return (
        <MultiSelect
          value={t.defaultValue ?? []}
          options={tagOptions[key] ?? []}
          optionLabel={'label'}
          optionValue={'value'}
          placeholder={tr('SnSettings.noDefault', 'No default')}
          onChange={e => onDefaultChange(side, key, t.defaultValue ?? [], e.value ?? [])}
          className={'w-full'}
        />
      );
    return <Chips value={t.defaultValue} onChange={e => onDefaultChange(side, key, t.defaultValue ?? [], e.value ?? [])} className={'w-full'} />;
  };

  // A labelled field: caption above its control.
  const field = (label: string, control: ReactNode): ReactNode => (
    <div className={'flex flex-col gap-1'}>
      <span className={'text-xs text-content-tertiary'}>{label}</span>
      {control}
    </div>
  );

  // One Owner|Inheritor panel pair per tag; each panel stacks its fields so nothing is cramped.
  const tagCard = (key: TagEnum): ReactNode => (
    <div key={key} className={'flex flex-col gap-2 border border-content-tertiary rounded-lg p-3'}>
      <COHeadline title={tr(`SnTag.${key}`, key)} variant={'h4'} />
      <div className={'flex flex-row gap-3'}>
        {SIDES.map(side => {
          const t = tagOf(side, key);
          return (
            <div key={side} className={'flex-1 flex flex-col gap-2 border border-content-tertiary rounded-lg p-2'}>
              <div className={'text-xs font-semibold uppercase text-content-tertiary'}>{sideLabel(side)}</div>
              {field(tr('SnSettings.modeShort', 'Mode (V·E·R)'), inlineTriple(side, `shippingTags.${key}`, t))}
              {field(tr('SnSettings.defaultValue', 'Default value'), defaultValueCell(side, key, t))}
              {field(tr('SnSettings.visibleWhen', 'Visible when'), conditionCell(side, key, 'visibleWhen', t.visibleWhen))}
              {field(tr('SnSettings.requiredWhen', 'Required when'), conditionCell(side, key, 'requiredWhen', t.requiredWhen))}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      <BreadCrumbComponent items={[{ label: tr('SnSettings.title', 'Supplier notification module settings') }]} />
      <COHeadline title={tr('SnSettings.title', 'Supplier notification module settings')} variant={'h2'} />

      <div className={'flex flex-col gap-4 max-w-[1450px]'}>
        <div className={'flex flex-col gap-4 max-w-[900px]'}>
          <COHeadline title={tr('SnSettings.generalConfig', 'General configuration')} variant={'h3'} />
          {generalBlock()}
        </div>

        <HorizontalLine variant={'tertiary'} />
        <div className={'flex flex-col gap-4 max-w-[900px]'}>
          <COHeadline title={tr('SnSettings.orders', 'Orders')} variant={'h3'} />
          {ordersBlock()}
        </div>

        <HorizontalLine variant={'tertiary'} />
        <COHeadline title={tr('SnSettings.shippingTags', 'Shipping information tags')} variant={'h3'} />
        <div className={'flex flex-col gap-3'}>{TAG_KEYS.map(key => tagCard(key))}</div>

        <HorizontalLine variant={'tertiary'} />
        <COHeadline title={tr('SnSettings.folders', 'Folders')} variant={'h3'} />
        <RingilInputSwitch
          value={settings.folders?.required === true}
          onChange={v => setSettings(p => ({ ...p, folders: { ...(p.folders ?? { required: false, restrictVisibility: false }), required: v } }))}
          label={tr('SnSettings.foldersRequired', 'Folder is required when creating a supplier notification')}
        />
        <RingilInputSwitch
          value={settings.folders?.restrictVisibility === true}
          onChange={v => setSettings(p => ({ ...p, folders: { ...(p.folders ?? { required: false, restrictVisibility: false }), restrictVisibility: v } }))}
          label={tr('SnSettings.foldersRestrictVisibility', 'Users only see supplier notifications in folders they have access to')}
        />

        <RingilButton label={tr('SnSettings.save', 'Save')} onClick={save} />
      </div>
    </div>
  );
};

export default ModuleSettingsSupplierNotification;
